import { useEffect, useRef, useState } from "react";
import { useMobile } from "../context/MobileContext";
import i18n from "../i18n";
import { colorAccent, colorAccentAlpha, colorBorder, colorForeground, colorMuted, colorMutedBg, colorNavBgSolid, colorPrimary } from "../rsc/colors";
import pempemLogo from "../rsc/pempem_logo_web.png";
import { MarketCountdown } from "./MarketCountdown";

const { version } = require('../../package.json')

type LocaleOption = { value: string; flag: string; label: string };

const localeOptions: LocaleOption[] = [
    { value: "en", flag: "🇬🇧", label: "EN" },
    { value: "id", flag: "🇮🇩", label: "ID" },
];

function PempemIcon({ size = 32 }: { size?: number }) {
    return (
        <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" style={{ width: size, height: size }} preserveAspectRatio="xMidYMid meet">
            <path fill={colorPrimary} d="m212.04892,45.82875c-44.27243,11.42511 -70.69317,26.42075 -102.82647,58.55405c-44.62943,44.62943 -62.12411,87.47383 -62.12411,151.74044c0,28.91979 -1.42802,36.41783 -8.92607,51.41347c-9.99709,19.63717 -10.7111,29.27679 -4.99854,50.34199c11.78212,41.77338 68.90814,58.19704 102.11246,29.27679c24.27871,-21.0652 29.27679,-52.84149 13.56715,-82.11828c-4.64154,-9.28308 -7.85459,-18.92316 -6.78357,-21.77921c3.21305,-7.85459 26.77775,-22.49322 40.70237,-24.99272c18.20869,-2.85651 36.41783,2.4995 59.26806,17.49467c29.27679,19.63717 51.05646,26.77775 79.26224,26.77775c43.20141,0 75.69172,-17.85168 95.68589,-52.12748c13.92462,-23.56424 17.13767,-20.35118 14.63863,13.56715c-3.92753,55.34053 -26.06374,97.11392 -67.48012,128.89021c-35.34636,26.42075 -53.91251,32.49031 -102.82647,32.49031c-43.20141,0 -64.98062,-5.71256 -89.97334,-23.20723c-5.35555,-3.92753 -9.99709,-5.35555 -9.99709,-3.21305c0,2.14204 -8.21206,8.92607 -17.85168,14.99563l-17.85168,11.06811l16.42366,11.78212c32.84731,23.56424 91.75837,39.63089 131.03225,36.06083c27.84877,-2.4995 35.34636,-4.28453 61.41056,-13.21014c92.47238,-31.7763 155.66796,-134.24576 141.02934,-229.93166c-3.21305,-21.77921 -2.85651,-27.49176 1.78503,-36.41783c6.78357,-13.21014 6.78357,-44.98644 0,-58.19704c-12.13912,-23.56424 -41.41638,-38.55987 -67.83666,-34.27534c-11.78212,1.42802 -19.99418,6.06956 -32.1333,17.13767c-28.20578,26.42075 -30.34827,61.05309 -4.64154,89.97334l10.7111,12.49613l-9.28308,12.49613c-20.35118,26.42075 -56.76902,32.84731 -89.61587,15.35264c-9.99709,-4.99854 -27.49176,-14.28162 -39.63089,-20.35118c-49.27097,-26.06374 -103.89749,-14.63863 -136.3878,28.20578c-6.78357,8.92607 -13.56715,16.42366 -15.35264,17.13767c-6.78357,2.4995 -3.21305,-41.77338 5.71256,-68.90814c12.13912,-37.13185 29.6338,-61.7671 60.33908,-85.3318c49.62798,-37.84586 118.53612,-45.34344 173.87665,-18.56615c13.56715,6.42657 17.49467,7.14058 18.92316,3.21305c1.07102,-2.4995 9.28308,-9.28308 18.20869,-14.99563l16.42366,-10.35409l-15.35264,-9.28308c-29.99127,-17.49467 -79.97625,-31.06228 -112.82356,-30.34827c-11.06811,0 -32.1333,3.21305 -46.41492,7.14058l0,0l-0.00046,0l0,0.00184z" />
        </svg>
    );
}

interface HeaderProps {
    hasPrices?: boolean;
}

export function Header({ hasPrices = true }: HeaderProps) {
    const [langOpen, setLangOpen] = useState(false);
    const [currentLocale, setCurrentLocale] = useState(i18n.language?.startsWith("en") ? "en" : "id");
    const isMobile = useMobile();
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setLangOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    function switchLocale(locale: string) {
        setCurrentLocale(locale);
        i18n.changeLanguage(locale);
        localStorage.setItem("locale", locale);
        setLangOpen(false);
    }

    const selected = localeOptions.find(o => o.value === currentLocale) || localeOptions[1];

    return (
        <nav style={{
            width: "100%",
            zIndex: 200,
            borderBottom: `1px solid ${colorBorder}`,
            background: colorNavBgSolid,
        }}>
            <div style={{
                margin: "0",
                padding: "0 5px",
                display: "flex",
                height: isMobile ? 80 : 64,
                alignItems: "center",
                justifyContent: "space-between",
                gap: 5
            }}>
                {/* Logo */}
                <a href="https://pempem.io/" style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, textDecoration: "none" }} title={`v${version}`}>
                    {isMobile
                        ? <PempemIcon size={32} />
                        : <img src={pempemLogo} alt="PemPem" style={{ height: 40, width: "auto", objectFit: "contain" }} />
                    }
                </a>

                {/* Market Countdown (center) */}
                <MarketCountdown />

                {/* Language Dropdown */}
                <div ref={dropdownRef} style={{ position: "relative" }}>
                    <button
                        onClick={() => setLangOpen(!langOpen)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "6px 12px",
                            fontSize: 12,
                            fontFamily: "'Geist Mono', 'SF Mono', 'Fira Code', monospace",
                            letterSpacing: "-0.05em",
                            textTransform: "uppercase",
                            border: `1px solid ${langOpen ? colorAccent : colorBorder}`,
                            borderRadius: 9999,
                            color: langOpen ? colorForeground : colorMuted,
                            background: "transparent",
                            cursor: "pointer",
                            transition: "color 200ms, border-color 200ms",
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.color = colorForeground;
                            e.currentTarget.style.borderColor = colorAccent;
                        }}
                        onMouseLeave={e => {
                            if (!langOpen) {
                                e.currentTarget.style.color = colorMuted;
                                e.currentTarget.style.borderColor = colorBorder;
                            }
                        }}
                    >
                        <span>{selected.flag}</span>
                        <span>{selected.label}</span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: langOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 200ms" }}>
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>

                    {langOpen && (
                        <div style={{
                            position: "absolute",
                            right: 0,
                            marginTop: 8,
                            width: 160,
                            borderRadius: 8,
                            border: `1px solid ${colorBorder}`,
                            background: colorNavBgSolid,
                            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -4px rgba(0,0,0,0.2)",
                            padding: "4px 0",
                            zIndex: 50,
                        }}>
                            {localeOptions.map(opt => (
                                <button
                                    key={opt.value}
                                    onClick={() => switchLocale(opt.value)}
                                    style={{
                                        width: "100%",
                                        textAlign: "left",
                                        padding: "8px 16px",
                                        fontSize: 14,
                                        fontFamily: "'Public Sans', system-ui, sans-serif",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        border: "none",
                                        cursor: "pointer",
                                        transition: "color 200ms, background-color 200ms",
                                        color: opt.value === currentLocale ? colorAccent : colorMuted,
                                        background: opt.value === currentLocale ? colorAccentAlpha : "transparent",
                                    }}
                                    onMouseEnter={e => {
                                        if (opt.value !== currentLocale) {
                                            e.currentTarget.style.color = colorForeground;
                                            e.currentTarget.style.background = colorMutedBg;
                                        }
                                    }}
                                    onMouseLeave={e => {
                                        if (opt.value !== currentLocale) {
                                            e.currentTarget.style.color = colorMuted;
                                            e.currentTarget.style.background = "transparent";
                                        }
                                    }}
                                >
                                    <span>{opt.flag}</span>
                                    <span>{opt.label === "EN" ? "English" : "Bahasa Indonesia"}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
}